import { useMemo } from 'react'

import useStableValue from '~/hooks/useStableValue'

/** Indicates that a filter key was provided but with undefined/null value (waiting for value) */
export const EMPTY = Symbol('EMPTY')
export type Empty = typeof EMPTY

/**
 * Output type for normalized filters:
 * - Keys with defaults (in D): always present with the default's type
 * - Keys without defaults (in T but not D): optional, can be value, EMPTY, or undefined
 */
export type NormalizedFilters<T, D extends Partial<T>> = {
  [K in keyof D]-?: D[K]
} & {
  [K in Exclude<keyof T, keyof D>]?: T[K] | Empty
}

/**
 * Normalizes filter inputs against optional defaults.
 *
 * For keys WITH a default:
 * - Omitted in filters → use default
 * - Provided but empty → use default
 * - Provided with value → use that value
 *
 * For keys WITHOUT a default:
 * - Omitted in filters → key not in output (undefined when accessed)
 * - Provided but empty → EMPTY
 * - Provided with value → use that value
 *
 * @param filters - The input filters (may be partial or undefined)
 * @param defaults - Optional defaults; keys with defaults are always present in output
 *
 * Returns a stable reference unless the actual filter values change.
 */
export function useNormalizedFilters<T extends Record<string, unknown>, D extends Partial<T>>(
  filters: Partial<T> | undefined,
  defaults: D = {} as D
): NormalizedFilters<T, D> {
  const stableFilters = useStableValue(filters)
  const stableDefaults = useStableValue(defaults)

  return useMemo(() => {
    const result = {} as NormalizedFilters<T, D>

    // Process keys from defaults (always present in output)
    for (const key of Object.keys(stableDefaults) as Array<keyof D>) {
      const keyProvided = stableFilters !== undefined && key in stableFilters
      if (keyProvided) {
        const value = stableFilters?.[key as keyof T]
        // Key provided - use value unless undefined, then fall back to default
        result[key] = (value ?? stableDefaults[key]) as NormalizedFilters<T, D>[keyof D]
      } else {
        // Key not provided - use default
        result[key] = stableDefaults[key] as NormalizedFilters<T, D>[keyof D]
      }
    }

    // Process keys from filters that aren't in defaults
    if (stableFilters) {
      for (const key of Object.keys(stableFilters) as Array<keyof T>) {
        if (key in stableDefaults) continue // Already processed

        const value = stableFilters[key]
        if (value == null) {
          // Provided but empty (null or undefined)
          ;(result as Record<keyof T, unknown>)[key] = EMPTY
        } else {
          // Has a value (not null/undefined)
          ;(result as Record<keyof T, unknown>)[key] = value
        }
      }
    }

    return result
  }, [stableDefaults, stableFilters])
}
